import type {AxiosError} from "axios";
import {isAxiosError} from "axios";

/**
 * NestJS validation error constraint
 */
interface ValidationConstraints {
  [key: string]: string;
}

/**
 * NestJS validation error field
 */
interface ValidationErrorField {
  property: string;
  constraints?: ValidationConstraints;
  value?: any;
}

/**
 * NestJS validation error response
 */
interface ValidationErrorResponse {
  message: string;
  errors?: ValidationErrorField[];
}

/**
 * Checks if an error is a NestJS validation error (400 with specific format)
 */
function isValidationError(error: unknown): error is AxiosError<ValidationErrorResponse> {
  return (
    isAxiosError(error) &&
    error.response?.status === 400 &&
    error.response?.data?.message === "Validation error" &&
    Array.isArray(error.response?.data?.errors)
  );
}

/**
 * Formats validation errors into human-readable messages
 * @param error - The axios error from the API
 * @returns Array of error messages in format "fieldName: error message"
 */
function formatValidationErrors(error: AxiosError<ValidationErrorResponse>): string[] {
  const errors = error.response?.data?.errors || [];

  return errors.map((field) => {
    const fieldName = field.property;
    const constraints = field.constraints || {};
    const errorMessages = Object.values(constraints);

    if (errorMessages.length > 0) {
      return `${fieldName}: ${errorMessages.join(", ")}`;
    }

    return `${fieldName} : valeur invalide`;
  });
}

/**
 * Gets a user-friendly error message from any error
 * @param error - The error to format
 * @param defaultMessage - Default message if error cannot be parsed
 * @returns Human-readable error message
 */
function getMessage(error: unknown, defaultMessage = "Une erreur s'est produite"): string {
  // Handle validation errors
  if (isValidationError(error)) {
    const errors = formatValidationErrors(error);
    return errors.join("; ");
  }

  // Handle axios errors with message
  if (isAxiosError(error)) {
    return error.response?.data?.message || error.message || defaultMessage;
  }

  // Handle regular errors
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === "string") {
    return error;
  }

  return defaultMessage;
}

/**
 * Comme `getMessage`, mais ne fait confiance au message serveur que sur un refus 400
 * confirmé — tout autre statut (réseau, 500…) reste sur `defaultMessage`. Un 400 est le
 * seul statut que ce repo réserve à des messages pensés pour l'utilisateur (ex. guard
 * complétude légale #1169) ; sur les autres, `error.message` d'Axios est un texte
 * technique anglais (même garde que `getSubmissionErrorMessage`, #799).
 * @param error - The error to format
 * @param defaultMessage - Message affiché sur tout statut autre que 400
 */
function getKnownBadRequestMessage(error: unknown, defaultMessage: string): string {
  if (error instanceof DisplayableError) {
    return error.message;
  }
  const isKnownBadRequest = isAxiosError(error) && error.response?.status === 400;
  return isKnownBadRequest ? getMessage(error, defaultMessage) : defaultMessage;
}

// Erreur dont le message est rédigé pour l'utilisateur : affiché tel quel, jamais remplacé par un défaut.
export class DisplayableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DisplayableError";
  }
}

/**
 * Extracts validation errors as a field map for form display
 * @param error - The axios error from the API
 * @returns Object mapping field names to error messages
 */
function getFieldErrors(error: unknown): Record<string, string> {
  if (!isValidationError(error)) {
    return {};
  }

  const errors = error.response?.data?.errors || [];
  const fieldErrors: Record<string, string> = {};

  errors.forEach((field) => {
    const constraints = field.constraints || {};
    const errorMessages = Object.values(constraints);

    if (errorMessages.length > 0 && errorMessages[0]) {
      fieldErrors[field.property] = errorMessages[0]; // Use first error message
    } else {
      fieldErrors[field.property] = "Valeur invalide";
    }
  });

  return fieldErrors;
}

export const errorService = {
  getMessage,
  getKnownBadRequestMessage,
  getFieldErrors,
};
